import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { CompositionSlot } from './CompositionSlot';
import { useCreateMenu } from '@/hooks/useMenus';
import { useToast } from '@/components/ui/ToastProvider';
import { generateMenu } from '@/lib/api/menus';
import type { GeneratedMenu, MealType } from '@/types/api';
import { formatDateForAPI } from '@/lib/utils/date';

type DecisionStatus =
  | 'pending'
  | 'accepted'
  | 'swapped'
  | 'skipped'
  | 'saved';

interface SlotDecision {
  mealId: number | null;
  mealName: string | null;
  status: DecisionStatus;
}

export interface MenuComposerProps {
  weekStart: Date;
  organizerId: number;
}

const SLOT_KEY_SEPARATOR = '|';

function slotKey(date: string, mealType: string): string {
  return `${date}${SLOT_KEY_SEPARATOR}${mealType}`;
}

function parseSlotKey(key: string): { date: string; mealType: MealType } {
  const idx = key.indexOf(SLOT_KEY_SEPARATOR);
  return {
    date: key.slice(0, idx),
    mealType: key.slice(idx + 1) as MealType,
  };
}

export function MenuComposer({ weekStart, organizerId }: MenuComposerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createMenuMutation = useCreateMenu();
  const toast = useToast();

  const [generatedMenu, setGeneratedMenu] = useState<GeneratedMenu | null>(
    null,
  );
  const [decisions, setDecisions] = useState<Map<string, SlotDecision>>(
    new Map(),
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState({ done: 0, total: 0 });
  const [saveErrors, setSaveErrors] = useState<string[]>([]);

  const abortControllerRef = useRef<AbortController | null>(null);
  const weekStartStr = formatDateForAPI(weekStart);

  // Generate menu — cancels any in-flight request
  const triggerGeneration = () => {
    if (isSaving) return;

    // Abort previous request
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Reset state
    setGeneratedMenu(null);
    setDecisions(new Map());
    setIsGenerating(true);
    setGenerationError(false);
    setSaveErrors([]);

    generateMenu(weekStartStr, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;

        setGeneratedMenu(data);
        const initial = new Map<string, SlotDecision>();
        for (const slot of data.slots) {
          initial.set(slotKey(slot.date, slot.mealType), {
            mealId: slot.suggestion?.mealId ?? null,
            mealName: slot.suggestion?.mealName ?? null,
            status: 'pending',
          });
        }
        setDecisions(initial);
        setIsGenerating(false);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        setGenerationError(true);
        setIsGenerating(false);
      });
  };

  // Re-generate after a constraint is saved, preserving existing decisions
  const regenerateAfterConstraint = () => {
    if (isSaving) return;

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsGenerating(true);
    setGenerationError(false);

    // Snapshot current decisions to merge later
    const previousDecisions = new Map(decisions);

    generateMenu(weekStartStr, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;

        setGeneratedMenu(data);
        const merged = new Map<string, SlotDecision>();
        for (const slot of data.slots) {
          const key = slotKey(slot.date, slot.mealType);
          const prev = previousDecisions.get(key);

          // Preserve accepted, swapped, or saved decisions
          if (
            prev &&
            (prev.status === 'accepted' ||
              prev.status === 'swapped' ||
              prev.status === 'saved')
          ) {
            merged.set(key, prev);
          } else {
            // Update pending/skipped slots with new suggestion
            merged.set(key, {
              mealId: slot.suggestion?.mealId ?? null,
              mealName: slot.suggestion?.mealName ?? null,
              status: 'pending',
            });
          }
        }
        setDecisions(merged);
        setIsGenerating(false);
        toast.success(t('constraint.saved'));
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        setGenerationError(true);
        setIsGenerating(false);
      });
  };

  // Generate on mount and when week changes
  useEffect(() => {
    triggerGeneration();
    return () => {
      abortControllerRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStartStr]);

  const updateDecision = (key: string, update: Partial<SlotDecision>) => {
    if (isSaving) return;
    setDecisions((prev) => {
      const next = new Map(prev);
      const current = next.get(key);
      if (current && current.status !== 'saved') {
        next.set(key, { ...current, ...update });
      }
      return next;
    });
  };

  // Count stats
  const decidedCount = Array.from(decisions.values()).filter(
    (d) => d.status !== 'pending',
  ).length;
  const totalSlots = generatedMenu?.slots.length ?? 0;
  const slotsToSave = Array.from(decisions.entries()).filter(
    ([, d]) =>
      (d.status === 'accepted' || d.status === 'swapped') && d.mealId,
  );

  // Validate and save
  const handleValidate = async () => {
    if (slotsToSave.length === 0 || isSaving) return;

    setIsSaving(true);
    setSaveProgress({ done: 0, total: slotsToSave.length });
    setSaveErrors([]);

    let done = 0;
    const errors: string[] = [];

    for (const [key, decision] of slotsToSave) {
      const { date, mealType } = parseSlotKey(key);
      try {
        await createMenuMutation.mutateAsync({
          date,
          mealType,
          tier: 'cheat',
          label: decision.mealName || `Meal ${decision.mealId}`,
          createdBy: organizerId,
        });
        done++;
        setSaveProgress({ done, total: slotsToSave.length });
        // Mark as saved so retries skip this slot
        setDecisions((prev) => {
          const next = new Map(prev);
          const current = next.get(key);
          if (current) {
            next.set(key, { ...current, status: 'saved' });
          }
          return next;
        });
      } catch (err) {
        console.error(`Failed to save menu for ${date} ${mealType}:`, err);
        errors.push(
          `${date} ${mealType === 'lunch' ? t('availability.lunch') : t('availability.dinner')}`,
        );
      }
    }

    setIsSaving(false);

    if (errors.length === 0) {
      navigate({ to: '/menu' });
    } else {
      setSaveErrors(errors);
    }
  };

  // Group slots by date
  const groupedSlots = generatedMenu?.slots.reduce(
    (acc, slot) => {
      if (!acc[slot.date]) acc[slot.date] = [];
      acc[slot.date].push(slot);
      return acc;
    },
    {} as Record<string, typeof generatedMenu.slots>,
  );

  // Map 'saved' status back to 'accepted' for display purposes
  const displayStatus = (
    status: DecisionStatus,
  ): 'pending' | 'accepted' | 'swapped' | 'skipped' => {
    if (status === 'saved') return 'accepted';
    return status;
  };

  return (
    <div className="space-y-6">
      {/* Generate button + meta */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <button
          onClick={triggerGeneration}
          disabled={isGenerating || isSaving}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {isGenerating ? t('compose.generating') : t('compose.regenerate')}
        </button>

        {generatedMenu && (
          <div className="flex gap-4 text-sm text-gray-600">
            <span>
              {t('compose.slotsDecided', {
                decided: decidedCount,
                total: totalSlots,
              })}
            </span>
            <span>
              {t('compose.cheatMeals')}: {generatedMenu.meta.cheatMealCount}
            </span>
            <span>
              {t('compose.healthyRatio')}:{' '}
              {Math.round(generatedMenu.meta.healthyRatio * 100)}%
            </span>
          </div>
        )}
      </div>

      {/* Loading state */}
      {isGenerating && (
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-r-transparent" />
          <p className="mt-4 text-gray-600">{t('compose.generating')}</p>
        </div>
      )}

      {/* Error state */}
      {generationError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">
            {t('compose.generationError')}
          </p>
        </div>
      )}

      {/* Save errors */}
      {saveErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm font-medium text-red-700 mb-1">
            {t('compose.savePartialError')}
          </p>
          <ul className="text-sm text-red-600 list-disc list-inside">
            {saveErrors.map((err) => (
              <li key={err}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Slots grid */}
      {groupedSlots && (
        <div className="space-y-6">
          {Object.entries(groupedSlots).map(([date, slots]) => (
            <div key={date}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {slots.map((slot) => {
                  const key = slotKey(slot.date, slot.mealType);
                  const decision = decisions.get(key);
                  const status = decision?.status ?? 'pending';

                  return (
                    <CompositionSlot
                      key={key}
                      slot={slot}
                      status={displayStatus(status)}
                      selectedMealId={decision?.mealId ?? null}
                      selectedMealName={decision?.mealName ?? null}
                      disabled={isSaving || status === 'saved'}
                      onAccept={() => {
                        // When accepting, restore the suggestion's mealId/mealName
                        // if no swap was done, or keep the swapped meal
                        const current = decisions.get(key);
                        if (current?.status === 'swapped') {
                          // Already swapped — just mark as accepted with the swapped meal
                          updateDecision(key, { status: 'accepted' });
                        } else {
                          // Accept the original suggestion
                          updateDecision(key, {
                            status: 'accepted',
                            mealId: slot.suggestion?.mealId ?? null,
                            mealName: slot.suggestion?.mealName ?? null,
                          });
                        }
                      }}
                      onSkip={() =>
                        updateDecision(key, {
                          status: 'skipped',
                          mealId: null,
                          mealName: null,
                        })
                      }
                      onSwap={(mealId, mealName) =>
                        updateDecision(key, {
                          status: 'swapped',
                          mealId,
                          mealName,
                        })
                      }
                      onConstraintSaved={regenerateAfterConstraint}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Validate button */}
      {generatedMenu && slotsToSave.length > 0 && (
        <div className="sticky bottom-4 bg-white border border-gray-200 rounded-lg shadow-lg p-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {isSaving
              ? t('compose.savingProgress', {
                  done: saveProgress.done,
                  total: saveProgress.total,
                })
              : t('compose.readyToSave', { count: slotsToSave.length })}
          </div>
          <button
            onClick={handleValidate}
            disabled={isSaving}
            className="px-6 py-2 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            {isSaving ? t('app.saving') : t('compose.validateMenu')}
          </button>
        </div>
      )}
    </div>
  );
}
